import { useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { canSeeClass, resolvePoolBalance } from '../../../utils/concessions';

/**
 * Custom hook for managing class data and booking operations
 */
export function useClasses() {
  const { user, refreshUserData } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);

  /**
   * Fetch classes from the API
   */
  const fetchClasses = useCallback(async (search = '') => {
    try {
      const params = new URLSearchParams({
        search,
        limit: 50
      });

      const response = await axios.get(`/api/classes?${params}`);
      // Client-side visibility filter (admin fetches elsewhere stay unfiltered):
      // untagged classes are visible to everyone; tagged classes only to marked users.
      setClasses((response.data?.classes || []).filter(
        (cls) => canSeeClass(cls, user)
      ));
    } catch (error) {
      toast.error('Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Fetch fresh data for a single class
   */
  const fetchClassDetails = useCallback(async (classId) => {
    try {
      const response = await axios.get(`/api/classes/${classId}`);
      const freshClassData = response.data;
      
      return {
        ...freshClassData,
        isUserBooked: freshClassData.isUserBooked || false,
        isUserOnWaitlist: freshClassData.isUserOnWaitlist || false,
        availableSpots: freshClassData.availableSpots || (freshClassData.maxCapacity - freshClassData.currentBookings),
        isFull: freshClassData.isFull || ((freshClassData.maxCapacity - freshClassData.currentBookings) === 0)
      };
    } catch (error) {
      return null;
    }
  }, []);

  /**
   * Select a class and fetch fresh data
   */
  const selectClass = useCallback(async (classItem) => {
    const freshData = await fetchClassDetails(classItem._id);
    setSelectedClass(freshData || classItem);
  }, [fetchClassDetails]);

  /**
   * Clear selected class
   */
  const clearSelectedClass = useCallback(() => {
    setSelectedClass(null);
  }, []);

  /**
   * Book a class
   */
  const bookClass = useCallback(async (classItem) => {
    try {
      const response = await axios.post('/api/bookings/book', {
        classId: classItem._id
      });
      
      if (response.data.success) {
        toast.success(`Successfully booked ${classItem.name}!`);
        await refreshUserData();
        return true;
      } else {
        toast.error(response.data.message || 'Failed to book class');
        return false;
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to book class';
      toast.error(message);
      return false;
    }
  }, [refreshUserData]);

  /**
   * Cancel a booking
   */
  const cancelBooking = useCallback(async (classItem) => {
    try {
      if (!classItem.userBookingId) {
        toast.error('Booking ID not found');
        return false;
      }

      const response = await axios.put(`/api/bookings/cancel/${classItem.userBookingId}`);
      
      if (response.data.message) {
        toast.success(response.data.message);
        await refreshUserData();
        return true;
      } else {
        toast.error('Failed to cancel booking');
        return false;
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to cancel booking';
      toast.error(message);
      return false;
    }
  }, [refreshUserData]);

  /**
   * Join a waitlist
   */
  const joinWaitlist = useCallback(async (classItem) => {
    try {
      const response = await axios.post(`/api/bookings/waitlist/${classItem._id}`);
      
      if (response.data.success) {
        toast.success(`Added to waitlist for ${classItem.name}!`);
        return true;
      } else {
        toast.error(response.data.message || 'Failed to join waitlist');
        return false;
      }
    } catch (error) {
      if (error.response?.data?.message) {
        if (error.response.data.message.includes('Insufficient concessions')) {
          toast.error(error.response.data.message, { duration: 6000 });
        } else {
          toast.error(error.response.data.message);
        }
      } else {
        toast.error('Failed to join waitlist');
      }
      return false;
    }
  }, []);

  /**
   * Leave a waitlist
   */
  const leaveWaitlist = useCallback(async (classItem) => {
    try {
      const response = await axios.delete(`/api/bookings/waitlist/${classItem._id}`);
      
      if (response.data.success) {
        toast.success(`Removed from waitlist for ${classItem.name}`);
        return true;
      } else {
        toast.error(response.data.message || 'Failed to leave waitlist');
        return false;
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to leave waitlist');
      }
      return false;
    }
  }, []);

  // Time-based check helpers
  const isWithin30Minutes = useCallback((classItem) => {
    const now = new Date();
    const classStartTime = new Date(classItem.startTime);
    const timeDifference = classStartTime.getTime() - now.getTime();
    const minutesDifference = timeDifference / (1000 * 60);
    return minutesDifference <= 30;
  }, []);

  const isWithin1Hour = useCallback((classItem) => {
    const now = new Date();
    const classStartTime = new Date(classItem.startTime);
    const timeDifference = classStartTime.getTime() - now.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference <= 1;
  }, []);

  const isWithin24Hours = useCallback((classItem) => {
    const now = new Date();
    const classStartTime = new Date(classItem.startTime);
    const timeDifference = classStartTime.getTime() - now.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference <= 24;
  }, []);

  const isAtConcessionLimit = useCallback((classItem) => {
    if (!user) return false;
    const concessionsRequired = classItem.concessionsRequired || 1;
    const userConcessions = resolvePoolBalance(user, classItem.concessionType);
    const minAllowedConcessions = -2;
    return (userConcessions - concessionsRequired) < minAllowedConcessions;
  }, [user]);

  const canJoinWaitlist = useCallback((classItem) => {
    const concessionsRequired = classItem.concessionsRequired || 1;
    return resolvePoolBalance(user, classItem.concessionType) >= concessionsRequired;
  }, [user]);

  const isUserBooked = useCallback((classItem) => {
    return classItem.isUserBooked || false;
  }, []);

  const isUserOnWaitlist = useCallback((classItem) => {
    return classItem.isUserOnWaitlist || false;
  }, []);

  return {
    // State
    classes,
    loading,
    selectedClass,
    user,
    
    // Data operations
    fetchClasses,
    fetchClassDetails,
    selectClass,
    clearSelectedClass,
    
    // Booking operations
    bookClass,
    cancelBooking,
    joinWaitlist,
    leaveWaitlist,
    
    // Time checks
    isWithin30Minutes,
    isWithin1Hour,
    isWithin24Hours,
    isAtConcessionLimit,
    canJoinWaitlist,
    isUserBooked,
    isUserOnWaitlist
  };
}

export default useClasses;
